import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, CheckCheck, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty, LicenseStatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ApproveDialog, BulkApproveDialog, ReasonDialog } from "@/components/LicenseDialogs";
import { RenewalRequests } from "@/components/RenewalRequests";

export default function Requests() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [approve, setApprove] = useState(null);
  const [reject, setReject] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const load = () => api.get("/licenses").then((r) => { setRows(r.data); setSelected([]); });
  useEffect(() => { load(); }, []);
  if (!rows) return <Loading />;
  const pending = rows.filter((r) => r.status === "pending");
  const decided = rows.filter((r) => r.status !== "pending").slice(0, 30);
  const isAdmin = user.role === "admin";
  const allSelected = pending.length > 0 && selected.length === pending.length;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div>
      <PageHeader eyebrow="Workflow nazionale" title="Richieste di licenza"
        subtitle={isAdmin ? "Approva o respingi le richieste inoltrate dai settori. Solo la sede nazionale può rilasciare licenze." : "Stato delle richieste inoltrate dal tuo settore. Per una nuova richiesta apri la scheda dell'atleta."} />
      <RenewalRequests onForwarded={load} />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="font-display text-lg font-semibold">In attesa di approvazione <span className="text-slate-400">({pending.length})</span></div>
        {isAdmin && pending.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? pending.map((l) => l.id) : [])} data-testid="requests-select-all-checkbox" />
              Seleziona tutte
            </label>
            <Button onClick={() => setBulkOpen(true)} disabled={!selected.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="requests-bulk-approve-btn">
              <CheckCheck className="mr-2 h-4 w-4" />Approva selezionate ({selected.length})
            </Button>
          </div>
        )}
      </div>
      {pending.length === 0 ? <Empty>Nessuna richiesta in attesa.</Empty> : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="pending-requests-list">
          {pending.map((l) => (
            <div key={l.id} className="card-surface p-5" data-testid={`request-card-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {isAdmin && <Checkbox className="mt-1.5" checked={selected.includes(l.id)} onCheckedChange={() => toggle(l.id)} aria-label={`Seleziona ${l.athlete_name}`} data-testid={`request-select-checkbox-${l.id}`} />}
                  <div>
                  <Link to={`/licenze/${l.id}`} className="font-display text-lg font-semibold text-slate-900 hover:underline">{l.athlete_name}</Link>
                  <div className="font-mono text-xs text-slate-500">Tessera {l.membership_number}</div>
                  </div>
                </div>
                <LicenseStatusBadge status={l.status} />
              </div>
              <div className="mt-3 text-sm text-slate-700"><b>{l.discipline_name}</b> · {l.categories.map((c) => c.name).join(", ")}</div>
              <div className="mt-1 text-xs text-slate-500">{l.sector_name} · richiesta da {l.requested_by_name} il {fmtDateTime(l.created_at)}</div>
              {l.notes && <div className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-600">{l.notes}</div>}
              {isAdmin && (
                <div className="mt-4 flex gap-2">
                  <Button onClick={() => setApprove(l)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`request-approve-btn-${l.id}`}><CheckCircle2 className="mr-2 h-4 w-4" />Approva</Button>
                  <Button variant="outline" onClick={() => setReject(l)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`request-reject-btn-${l.id}`}><XCircle className="mr-2 h-4 w-4" />Respingi</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mb-3 mt-10 font-display text-lg font-semibold">Richieste evase di recente</div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Atleta</th><th className="px-4 py-3">Numero</th><th className="px-4 py-3">Disciplina</th><th className="px-4 py-3">Emissione</th><th className="px-4 py-3">Stato</th></tr>
          </thead>
          <tbody>
            {decided.map((l) => (
              <tr key={l.id} className="border-b border-slate-100">
                <td className="px-4 py-3"><Link to={`/licenze/${l.id}`} className="font-medium hover:underline">{l.athlete_name}</Link></td>
                <td className="px-4 py-3 font-mono text-xs">{l.license_number || "—"}</td>
                <td className="px-4 py-3">{l.discipline_name}</td>
                <td className="px-4 py-3">{fmtDate(l.issue_date)}</td>
                <td className="px-4 py-3"><LicenseStatusBadge status={l.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BulkApproveDialog open={bulkOpen} onOpenChange={setBulkOpen} items={pending.filter((l) => selected.includes(l.id))} onDone={load} />
      <ApproveDialog open={!!approve} onOpenChange={(v) => !v && setApprove(null)} lic={approve} onDone={load} />
      <ReasonDialog open={!!reject} onOpenChange={(v) => !v && setReject(null)} title="Respingi richiesta" confirmLabel="Respingi" danger
        onConfirm={async (reason) => { await api.post(`/licenses/${reject.id}/reject`, { reason }); toast.success("Richiesta respinta"); load(); }} />
    </div>
  );
}
